import React, { useState, useMemo } from "react";
import styles from "./ScheduleModal.module.css";

interface ScheduleModalProps {
    onSchedule: (datetime: Date) => void;
    onClose: () => void;
    initialDate?: string | null; // ISO string for pre-populating (reschedule)
}

const ScheduleModal: React.FC<ScheduleModalProps> = ({
    onSchedule,
    onClose,
    initialDate,
}) => {
    // Parse initial date or default to tomorrow
    const getInitialValues = () => {
        if (initialDate) {
            const date = new Date(initialDate);
            return {
                date: date.toISOString().split("T")[0],
                time: `${date.getUTCHours().toString().padStart(2, "0")}:${date.getUTCMinutes().toString().padStart(2, "0")}`,
            };
        }
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return {
            date: tomorrow.toISOString().split("T")[0],
            time: "14:00",
        };
    };

    const initial = getInitialValues();
    const [selectedDate, setSelectedDate] = useState<string>(initial.date);
    const [selectedTime, setSelectedTime] = useState<string>(initial.time);
    const [submitting, setSubmitting] = useState(false);

    // Generate time options in 30-minute increments
    const timeOptions = useMemo(() => {
        const options: string[] = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const h = hour.toString().padStart(2, "0");
                const m = minute.toString().padStart(2, "0");
                options.push(`${h}:${m}`);
            }
        }
        return options;
    }, []);

    // Quick schedule options - memoized with date dependency
    const quickOptions = useMemo(() => {
        const now = new Date();
        const today = new Date(now);
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        return [
            {
                label: "Today 2pm",
                date: today.toISOString().split("T")[0],
                time: "14:00",
                disabled: currentHour > 14 || (currentHour === 14 && currentMinute > 0),
            },
            {
                label: "Today 6pm",
                date: today.toISOString().split("T")[0],
                time: "18:00",
                disabled: currentHour > 18 || (currentHour === 18 && currentMinute > 0),
            },
            {
                label: "Tomorrow 10am",
                date: tomorrow.toISOString().split("T")[0],
                time: "10:00",
                disabled: false,
            },
            {
                label: "Tomorrow 2pm",
                date: tomorrow.toISOString().split("T")[0],
                time: "14:00",
                disabled: false,
            },
        ];
    }, []);

    const handleQuickSelect = (date: string, time: string) => {
        setSelectedDate(date);
        setSelectedTime(time);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            // Create UTC date from selected date and time
            const [hours, minutes] = selectedTime.split(":").map(Number);
            const datetime = new Date(selectedDate + "T" + selectedTime + ":00Z");

            // Validate it's in the future (compare in UTC)
            if (datetime <= new Date()) {
                alert("Please select a time in the future");
                setSubmitting(false);
                return;
            }

            await onSchedule(datetime);
        } finally {
            setSubmitting(false);
        }
    };

    // Format time for display (12-hour format)
    const formatTimeLabel = (time: string): string => {
        const [hours, minutes] = time.split(":").map(Number);
        const period = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
    };

    // Get local time equivalent of selected UTC time
    const getLocalTime = (): string => {
        const datetime = new Date(selectedDate + "T" + selectedTime + ":00Z");
        return datetime.toLocaleString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            timeZoneName: "short",
        });
    };

    // Get Riyadh time equivalent
    const getRiyadhTime = (): string => {
        const datetime = new Date(selectedDate + "T" + selectedTime + ":00Z");
        return datetime.toLocaleString("en-US", {
            timeZone: "Asia/Riyadh",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>{initialDate ? "Reschedule Post" : "Schedule Post"}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {/* Quick Select Options */}
                    <div className={styles.section}>
                        <label className={styles.sectionLabel}>Quick Select</label>
                        <div className={styles.quickOptions}>
                            {quickOptions.map((option) => (
                                <button
                                    key={`${option.date}-${option.time}`}
                                    className={`${styles.quickOption} ${
                                        selectedDate === option.date && selectedTime === option.time
                                            ? styles.quickOptionActive
                                            : ""
                                    }`}
                                    onClick={() => handleQuickSelect(option.date, option.time)}
                                    disabled={option.disabled}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Date Picker */}
                    <div className={styles.section}>
                        <label className={styles.sectionLabel}>Date (UTC)</label>
                        <input
                            type="date"
                            className={styles.dateInput}
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            min={new Date().toISOString().split("T")[0]}
                        />
                    </div>

                    {/* Time Picker */}
                    <div className={styles.section}>
                        <label className={styles.sectionLabel}>Time (UTC)</label>
                        <select
                            className={styles.timeSelect}
                            value={selectedTime}
                            onChange={(e) => setSelectedTime(e.target.value)}
                        >
                            {timeOptions.map((time) => (
                                <option key={time} value={time}>
                                    {formatTimeLabel(time)}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Timezone Info */}
                    <div className={styles.timezoneInfo}>
                        <div className={styles.timezoneRow}>
                            <span className={styles.timezoneLabel}>Your local time:</span>
                            <span className={styles.timezoneValue}>{getLocalTime()}</span>
                        </div>
                        <div className={styles.timezoneRow}>
                            <span className={styles.timezoneLabel}>Riyadh time:</span>
                            <span className={styles.timezoneValue}>{getRiyadhTime()}</span>
                        </div>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button className={styles.secondaryButton} onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className={styles.primaryButton}
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? "Scheduling..." : initialDate ? "Reschedule" : "Schedule"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScheduleModal;
